import { ReactNode } from 'react';
import { Box } from '@mui/material';

/**
 * 「OHANA AI の機能」6枚のカードの画面イメージ枠に置く線画。
 *
 * 以前は3枚に実画面のキャプチャを使っていたが、線画3枚と混在して質感が揃わないため
 * 6枚すべて線画に統一した。実画面は ConversionSection と機能詳細ページ(/features)の
 * FeatureSection に残っているので、製品が実在することはサイト全体としては伝わる。
 *
 * あえて「画面らしく」描いていない。ウィンドウ枠やボタンを描くと実物と異なるモック
 * 画像に見えてしまい、誤解を招くため。機能の中身を表す図解に留めている。
 */

const TEAL = '#2aa1af'; // OHANA AI のブランドカラー
const PALE = '#eef7f8'; // ティールの淡色（面の塗り）
const RED = '#e2574c'; // 期限を示す丸だけに使う

/**
 * 6点で線幅・色・viewBox(16:10)を揃えるための共通ラッパ。
 *
 * 置き場所の枠は高さ固定の横長なので、幅ではなく高さを基準に収める。
 * 幅基準にすると枠の高さを超えて線画が切れる。
 */
const Art = ({ children }: { children: ReactNode }) => (
    <Box
        component="svg"
        viewBox="0 0 200 125"
        role="presentation"
        aria-hidden="true"
        sx={{ height: '80%', width: 'auto', maxWidth: '80%', display: 'block' }}
    >
        <g fill="none" stroke={TEAL} strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
            {children}
        </g>
    </Box>
);

/** 加算取得をサポート：複数の書類をAIが下書きし、加算につながる */
export const ArtAddon = () => (
    <Art>
        <g transform="rotate(-13 67 62)">
            <rect x="40" y="26" width="54" height="72" rx="5" fill={PALE} />
        </g>
        <rect x="58" y="22" width="54" height="76" rx="5" fill="#fff" />
        <g transform="rotate(13 103 62)">
            <rect x="76" y="26" width="54" height="72" rx="5" fill="#fff" />
            <path d="M86 46 h34 M86 58 h34 M86 70 h22" />
        </g>
        <path d="M152 18 l4 10 l10 4 l-10 4 l-4 10 l-4 -10 l-10 -4 l10 -4 z" fill={PALE} />
        <path d="M174 44 l2.6 6 l6 2.6 l-6 2.6 l-2.6 6 l-2.6 -6 l-6 -2.6 l6 -2.6 z" fill={PALE} />
        <circle cx="160" cy="94" r="17" fill={PALE} />
        <path d="M160 85 v18 M151 94 h18" strokeWidth={4.5} />
    </Art>
);

/** 減算対策とアラート：期限の迫った日を事前に知らせる */
export const ArtDeadline = () => (
    <Art>
        <rect x="24" y="26" width="104" height="88" rx="8" fill="#fff" />
        <path d="M24 50 h104" />
        <path d="M50 14 v18 M102 14 v18" strokeWidth={4.5} />
        <g fill={TEAL} stroke="none">
            <circle cx="42" cy="66" r="4" />
            <circle cx="64" cy="66" r="4" />
            <circle cx="86" cy="66" r="4" />
            <circle cx="108" cy="66" r="4" />
            <circle cx="42" cy="86" r="4" />
            <circle cx="64" cy="86" r="4" />
            <circle cx="108" cy="86" r="4" />
            <circle cx="42" cy="104" r="4" />
            <circle cx="64" cy="104" r="4" />
        </g>
        <circle cx="86" cy="86" r="11" stroke={RED} strokeWidth={4} />
        <path d="M156 20 c-12 0 -20 9 -20 20 c0 13 -4 17 -7 21 h54 c-3 -4 -7 -8 -7 -21 c0 -11 -8 -20 -20 -20 z" fill={PALE} />
        <path d="M156 14 v6" />
        <path d="M147 65 a9 9 0 0 0 18 0" />
        <path d="M184 28 l8 -6 M189 44 l10 -1 M184 60 l8 6" strokeWidth={3.5} />
    </Art>
);

/** 音声入力とAI下書き：話した内容が書類になる（マイク→波形→書類の順で左から読ませる） */
export const ArtVoice = () => (
    <Art>
        <rect x="22" y="24" width="26" height="42" rx="13" fill={PALE} />
        <path d="M14 58 a21 21 0 0 0 42 0" />
        <path d="M35 79 v14 M22 93 h26" />
        <path d="M68 48 v24 M80 38 v44 M92 46 v28" strokeWidth={6} />
        <rect x="118" y="22" width="62" height="80" rx="6" fill="#fff" />
        <path d="M130 44 h38 M130 58 h38 M130 72 h24" />
        <path d="M178 12 l3 7 l7 3 l-7 3 l-3 7 l-3 -7 l-7 -3 l7 -3 z" fill={PALE} />
    </Art>
);

/** 個別支援計画書の自動作成：アセスメントが計画書へそのまま流れ込む */
export const ArtPlan = () => (
    <Art>
        <rect x="12" y="32" width="48" height="62" rx="5" fill={PALE} />
        <path d="M22 50 h28 M22 62 h28 M22 74 h18" />
        <path d="M68 63 h18 M80 56 l7 7 l-7 7" strokeWidth={5} />
        <path d="M78 28 l2.6 6 l6 2.6 l-6 2.6 l-2.6 6 l-2.6 -6 l-6 -2.6 l6 -2.6 z" fill={PALE} />
        <rect x="100" y="14" width="84" height="98" rx="6" fill="#fff" />
        <rect x="112" y="28" width="36" height="7" rx={3.5} fill={TEAL} stroke="none" />
        {[50, 64, 78, 92].map((y) => (
            <g key={y}>
                <rect x="112" y={y - 4} width="8" height="8" rx="2" fill={PALE} />
                <path d={`M126 ${y} h44`} />
            </g>
        ))}
    </Art>
);

/** 5領域のアセスメント：領域ごとのバランスを横棒で並べ、吹き出しで対話式であることを示す */
export const ArtAssess = () => (
    <Art>
        {/* 5領域のバランス。多角形は形が読み取りにくいので、長さで比べられる横棒にしている */}
        <path d="M18 12 v100" strokeWidth={2.5} opacity={0.45} />
        {[
            [14, 86],
            [35, 58],
            [56, 104],
            [77, 70],
            [98, 44]
        ].map(([y, w]) => (
            <rect key={y} x="18" y={y} width={w} height="13" rx="6.5" fill={PALE} />
        ))}
        <path d="M136 16 h50 a7 7 0 0 1 7 7 v28 a7 7 0 0 1 -7 7 h-32 l-11 10 v-10 h-7 a7 7 0 0 1 -7 -7 v-28 a7 7 0 0 1 7 -7 z" fill="#fff" />
        <path d="M148 31 h28 M148 44 h18" />
    </Art>
);

/** モニタリングと成長データ：記録が積み上がり、右肩上がりに伸びていく */
export const ArtGrowth = () => (
    <Art>
        {[
            [46, 80],
            [80, 64],
            [114, 48],
            [148, 28]
        ].map(([x, y]) => (
            <rect key={x} x={x} y={y} width="22" height={104 - y} rx="3" fill={PALE} />
        ))}
        <path d="M57 76 L91 60 L125 44 L159 24" />
        {[
            [57, 76],
            [91, 60],
            [125, 44],
            [159, 24]
        ].map(([x, y]) => (
            <circle key={x} cx={x} cy={y} r={4.5} fill="#fff" />
        ))}
        <path d="M30 16 v88 h150" strokeWidth={3.5} />
    </Art>
);
